import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Close, Add, Delete } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import ClientAutocomplete from '../Common/ClientAutocomplete';
import {
  ASSIGNABLE_USERS_QUERY_KEY,
  asAssignableUsers,
  fetchAssignableUsers,
} from '../../lib/assignableUsers';
import { getAuthToken } from '../../lib/authToken';

const STEPS = ['Matter basics', 'Parties & conflicts', 'Matter details', 'Engagement'];

const OVERLAYS = [
  { id: 'personal_injury', label: 'Personal injury' },
  { id: 'estate_planning', label: 'Wills / estate planning' },
  { id: 'trusts', label: 'Trusts' },
  { id: 'financial_elder', label: 'Financial / elder' },
  { id: 'real_estate', label: 'Real estate / land' },
  { id: 'general_litigation', label: 'General litigation' },
  { id: 'family', label: 'Family' },
];

interface CaseIntakeWizardProps {
  open: boolean;
  onClose: () => void;
  mode?: 'create' | 'continue';
  caseId?: string;
  onCreated?: (caseId: string) => void;
  onSaved?: (caseId: string) => void;
}

const emptyAdverse = () => ({
  firstName: '',
  lastName: '',
  organizationName: '',
  alias: '',
  notes: '',
});

const US_JURISDICTIONS: { code: string; label: string }[] = [
  { code: 'federal', label: 'Federal' },
  { code: 'AL', label: 'Alabama' }, { code: 'AK', label: 'Alaska' }, { code: 'AZ', label: 'Arizona' },
  { code: 'AR', label: 'Arkansas' }, { code: 'CA', label: 'California' }, { code: 'CO', label: 'Colorado' },
  { code: 'CT', label: 'Connecticut' }, { code: 'DE', label: 'Delaware' }, { code: 'FL', label: 'Florida' },
  { code: 'GA', label: 'Georgia' }, { code: 'HI', label: 'Hawaii' }, { code: 'ID', label: 'Idaho' },
  { code: 'IL', label: 'Illinois' }, { code: 'IN', label: 'Indiana' }, { code: 'IA', label: 'Iowa' },
  { code: 'KS', label: 'Kansas' }, { code: 'KY', label: 'Kentucky' }, { code: 'LA', label: 'Louisiana' },
  { code: 'ME', label: 'Maine' }, { code: 'MD', label: 'Maryland' }, { code: 'MA', label: 'Massachusetts' },
  { code: 'MI', label: 'Michigan' }, { code: 'MN', label: 'Minnesota' }, { code: 'MS', label: 'Mississippi' },
  { code: 'MO', label: 'Missouri' }, { code: 'MT', label: 'Montana' }, { code: 'NE', label: 'Nebraska' },
  { code: 'NV', label: 'Nevada' }, { code: 'NH', label: 'New Hampshire' }, { code: 'NJ', label: 'New Jersey' },
  { code: 'NM', label: 'New Mexico' }, { code: 'NY', label: 'New York' }, { code: 'NC', label: 'North Carolina' },
  { code: 'ND', label: 'North Dakota' }, { code: 'OH', label: 'Ohio' }, { code: 'OK', label: 'Oklahoma' },
  { code: 'OR', label: 'Oregon' }, { code: 'PA', label: 'Pennsylvania' }, { code: 'RI', label: 'Rhode Island' },
  { code: 'SC', label: 'South Carolina' }, { code: 'SD', label: 'South Dakota' }, { code: 'TN', label: 'Tennessee' },
  { code: 'TX', label: 'Texas' }, { code: 'UT', label: 'Utah' }, { code: 'VT', label: 'Vermont' },
  { code: 'VA', label: 'Virginia' }, { code: 'WA', label: 'Washington' }, { code: 'WV', label: 'West Virginia' },
  { code: 'WI', label: 'Wisconsin' }, { code: 'WY', label: 'Wyoming' }, { code: 'DC', label: 'District of Columbia' },
];

interface IntakeRules {
  jurisdiction?: string | null;
  overlays?: string[];
  requiredFields?: string[];
  hiddenFields?: string[];
  notes?: string[];
  stepHints?: {
    matterBasics?: string;
    matterDetails?: string;
  };
}

const fieldLabel = (label: string, required: boolean) =>
  required ? `${label} *` : label;

const getDefaultForm = () => ({
  title: '',
  caseType: '',
  clientId: '',
  assignedTo: '',
  priority: 'medium',
  urgencyLevel: 'routine',
  jurisdiction: '',
  court: '',
  intakeSummary: '',
  matterOverlays: [] as string[],
  representationStatus: 'prospective',
  adverseParties: [emptyAdverse()],
  relatedNames: '',
  conflictCheckStatus: '' as string,
  conflictCheckNotes: '',
  questionnaireData: {
    personalInjury: {
      incidentDate: '',
      incidentType: '',
      incidentLocation: '',
      incidentDescription: '',
      injuriesReported: '',
      medicalProviders: '',
      recordsRequests: '',
      liens: '',
      wageLoss: '',
      policyLimits: '',
      insuranceCarrier: '',
    },
    estatePlanning: {
      estatePlanningGoals: '',
      fiduciaryNominations: '',
      beneficiaries: '',
      existingEstateDocuments: '',
      assetSchedule: '',
      liabilitySchedule: '',
      beneficiaryDesignations: '',
      numberOfChildren: '',
      additionalSpecificBequests: '',
      healthcareDirective: '',
      residualBeneficiaries: '',
    },
    trusts: {
      trustType: '',
      grantors: '',
      trustees: '',
      beneficiaries: '',
      assetSchedule: '',
    },
    financialElder: {
      capacityConcerns: false,
      caregivers: '',
      governmentBenefits: '',
      powerOfAttorneyStatus: '',
    },
    realEstate: {
      propertyAddress: '',
      propertyType: '',
      transactionType: '',
      closingDate: '',
      purchasePrice: '',
      knownTitleIssues: '',
      parcelId: '',
      titleCompany: '',
      closingChecklistNotes: '',
      earnestMoney: '',
    },
    generalLitigation: {
      disputeType: '',
      claimSummary: '',
      courtCaseNumber: '',
      nextHearingDate: '',
    },
    family: {
      opposingPartyRelationship: '',
      childrenInMatter: '',
      marriageDate: '',
      separationDate: '',
    },
  },
  privacyNoticeAcknowledged: false,
  communicationConsent: false,
  retainerSent: false,
  signatureName: '',
});

const mergeQuestionnaireData = (data: Record<string, any> | undefined) => {
  const defaults = getDefaultForm().questionnaireData;
  return {
    personalInjury: { ...defaults.personalInjury, ...(data?.personalInjury || {}) },
    estatePlanning: { ...defaults.estatePlanning, ...(data?.estatePlanning || {}) },
    trusts: { ...defaults.trusts, ...(data?.trusts || {}) },
    financialElder: { ...defaults.financialElder, ...(data?.financialElder || {}) },
    realEstate: { ...defaults.realEstate, ...(data?.realEstate || {}) },
    generalLitigation: { ...defaults.generalLitigation, ...(data?.generalLitigation || {}) },
    family: { ...defaults.family, ...(data?.family || {}) },
  };
};

const CaseIntakeWizard: React.FC<CaseIntakeWizardProps> = ({
  open,
  onClose,
  mode = 'create',
  caseId,
  onCreated,
  onSaved,
}) => {
  const isContinue = mode === 'continue';
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [intakeReady, setIntakeReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [conflictPreview, setConflictPreview] = useState<any>(null);
  const [checkingConflict, setCheckingConflict] = useState(false);

  const [form, setForm] = useState(getDefaultForm);

  const { data: intakeRules } = useQuery({
    queryKey: ['intake-rules', form.jurisdiction, form.matterOverlays.join(',')],
    enabled: open && Boolean(form.jurisdiction || form.matterOverlays.length > 0),
    queryFn: async (): Promise<IntakeRules> => {
      const token = getAuthToken();
      const params = new URLSearchParams();
      if (form.jurisdiction) params.set('jurisdiction', form.jurisdiction);
      if (form.matterOverlays.length) params.set('overlays', form.matterOverlays.join(','));
      const res = await fetch(`/api/case-intake/rules?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(await getApiErrorMessage(res, 'Unable to load intake rules'));
      }
      const json = await res.json();
      return json.data || {};
    },
  });

  const isFieldHidden = (fieldId: string) =>
    Boolean(intakeRules?.hiddenFields?.includes(fieldId));
  const isFieldRequired = (fieldId: string) =>
    Boolean(intakeRules?.requiredFields?.includes(fieldId));

  const { data: usersData } = useQuery({
    queryKey: ASSIGNABLE_USERS_QUERY_KEY,
    enabled: open,
    queryFn: fetchAssignableUsers,
  });
  const users = asAssignableUsers(usersData);

  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setConflictPreview(null);
      setIntakeReady(false);
      setLoadError(null);
      if (!isContinue) setForm(getDefaultForm());
      return;
    }
    if (!isContinue || !caseId) {
      if (!isContinue) setForm(getDefaultForm());
      setIntakeReady(true);
      return;
    }

    let cancelled = false;
    setIntakeReady(false);
    setLoadError(null);

    const loadIntake = async () => {
      try {
        const token = getAuthToken();
        const res = await fetch(`/api/case-intake/${caseId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          throw new Error(await getApiErrorMessage(res, 'Unable to load case intake.'));
        }
        const json = await res.json();
        if (cancelled) return;

        const caseData = json.data;
        if (!caseData) {
          throw new Error('Case intake data is unavailable.');
        }

        const questionnaire = caseData.intakeQuestionnaire ?? null;
        const adverseParties = (caseData.parties || [])
          .filter((p: any) => p.relationship === 'adverse')
          .map((p: any) => ({
            firstName: p.firstName || '',
            lastName: p.lastName || '',
            organizationName: p.organizationName || '',
            alias: p.alias || p.metadata?.alias || '',
            notes: p.notes || '',
          }));

        setForm({
          ...getDefaultForm(),
          title: caseData.title || '',
          caseType: caseData.caseType || '',
          clientId: caseData.clientId || '',
          assignedTo: caseData.assignedTo || '',
          priority: caseData.priority || 'medium',
          urgencyLevel: caseData.urgencyLevel || 'routine',
          jurisdiction: caseData.jurisdiction || '',
          court: caseData.court || '',
          intakeSummary: caseData.intakeSummary || '',
          matterOverlays: caseData.matterOverlays || questionnaire?.overlayTypes || [],
          representationStatus: caseData.representationStatus || 'prospective',
          adverseParties: adverseParties.length ? adverseParties : [emptyAdverse()],
          relatedNames: '',
          conflictCheckStatus: caseData.conflictCheckStatus || '',
          conflictCheckNotes: caseData.conflictCheckNotes || '',
          questionnaireData: mergeQuestionnaireData(questionnaire?.data),
        });
        setIntakeReady(true);
      } catch (err: unknown) {
        console.error('Continue intake load failed:', err);
        if (!cancelled) {
          const message = toUserErrorMessage(err, 'Unable to load case intake.');
          setLoadError(message);
          toast.error(message);
        }
      }
    };

    loadIntake();
    return () => { cancelled = true; };
  }, [open, isContinue, caseId]);

  const setField = (key: string, value: any) => setForm((p) => ({ ...p, [key]: value }));

  const toggleOverlay = (id: string) => {
    setForm((p) => ({
      ...p,
      matterOverlays: p.matterOverlays.includes(id)
        ? p.matterOverlays.filter((x) => x !== id)
        : [...p.matterOverlays, id],
    }));
  };

  const canNext = useMemo(() => {
    if (activeStep === 0) return Boolean(form.title.trim() && form.clientId);
    if (activeStep === 1) return true;
    return true;
  }, [activeStep, form.title, form.clientId]);

  const runConflictPreview = async () => {
    setCheckingConflict(true);
    try {
      const token = getAuthToken();
      const relatedNames = form.relatedNames
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const res = await fetch('/api/case-intake/conflict-check', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientId: form.clientId || null,
          adverseParties: form.adverseParties,
          relatedNames,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Conflict check failed');
      setConflictPreview(json.data);
      setField('conflictCheckStatus', json.data.status);
    } catch (err: any) {
      toast.error(err.message || 'Conflict check failed');
    } finally {
      setCheckingConflict(false);
    }
  };

  const persistSolFromIntake = async (savedCaseId: string, token: string | null) => {
    const incidentDate = (form.questionnaireData as { personalInjury?: { incidentDate?: string } })
      ?.personalInjury?.incidentDate;
    if (!incidentDate || !savedCaseId || !token) return;

    try {
      await fetch('/api/calendar/persist-sol', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          caseId: savedCaseId,
          incidentDate,
          jurisdiction: form.jurisdiction || undefined,
          overlays: form.matterOverlays,
          createCalendarEvent: true,
        }),
      });
    } catch {
      /* non-blocking */
    }
  };

  const handleSubmit = async () => {
    if (isContinue && !caseId) {
      toast.error('Unable to save intake — case is missing.');
      return;
    }

    setSubmitting(true);
    try {
      const token = getAuthToken();
      const relatedNames = form.relatedNames
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);

      const payload = {
        title: form.title,
        caseType: form.caseType || form.matterOverlays[0] || 'general',
        assignedTo: form.assignedTo || undefined,
        priority: form.priority,
        urgencyLevel: form.urgencyLevel,
        jurisdiction: form.jurisdiction || undefined,
        court: form.court || undefined,
        intakeSummary: form.intakeSummary,
        matterOverlays: form.matterOverlays,
        representationStatus: form.representationStatus,
        questionnaireData: form.questionnaireData,
        conflictCheckStatus: form.conflictCheckStatus || undefined,
        conflictCheckNotes: form.conflictCheckNotes || undefined,
      };

      if (isContinue && caseId) {
        const res = await fetch(`/api/case-intake/${caseId}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...payload,
            questionnaireStatus: 'submitted',
            adverseParties: form.adverseParties.filter(
              (p) => p.firstName || p.lastName || p.organizationName || p.alias
            ),
            relatedNames,
          }),
        });
        if (!res.ok) {
          throw new Error(await getApiErrorMessage(res, 'Failed to save intake'));
        }

        // Persist consents on continue (parity with create path)
        const consents = [];
        if (form.privacyNoticeAcknowledged) {
          consents.push({
            consentType: 'privacy_notice',
            accepted: true,
            signatureName: form.signatureName || undefined,
            documentVersion: '1.0',
          });
        }
        if (form.communicationConsent) {
          consents.push({
            consentType: 'electronic_communication',
            accepted: true,
            signatureName: form.signatureName || undefined,
          });
        }
        if (form.retainerSent) {
          consents.push({
            consentType: 'retainer_sent',
            accepted: true,
            metadata: { sentAtIntake: true },
          });
        }
        for (const c of consents) {
          try {
            await fetch(`/api/case-intake/${caseId}/consents`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(c),
            });
          } catch {
            /* non-blocking */
          }
        }

        toast.success('Case intake saved');
        await persistSolFromIntake(caseId, token);
        onSaved?.(caseId);
        onClose();
        setActiveStep(0);
        return;
      }

      const consents = [];
      if (form.privacyNoticeAcknowledged) {
        consents.push({
          consentType: 'privacy_notice',
          accepted: true,
          signatureName: form.signatureName || undefined,
          documentVersion: '1.0',
        });
      }
      if (form.communicationConsent) {
        consents.push({
          consentType: 'electronic_communication',
          accepted: true,
          signatureName: form.signatureName || undefined,
        });
      }
      if (form.retainerSent) {
        consents.push({
          consentType: 'retainer_sent',
          accepted: true,
          metadata: { sentAtIntake: true },
        });
      }

      const res = await fetch('/api/case-intake', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...payload,
          clientId: form.clientId,
          adverseParties: form.adverseParties.filter(
            (p) => p.firstName || p.lastName || p.organizationName || p.alias
          ),
          relatedNames,
          consents,
          runConflict: true,
        }),
      });
      if (!res.ok) {
        throw new Error(await getApiErrorMessage(res, 'Failed to create intake'));
      }
      const json = await res.json();

      toast.success('Case intake created');
      const newCaseId = json.data?.case?.id;
      if (newCaseId) {
        await persistSolFromIntake(newCaseId, token);
      }
      onCreated?.(newCaseId);
      onClose();
      setActiveStep(0);
    } catch (err: unknown) {
      toast.error(toUserErrorMessage(err, `Failed to ${isContinue ? 'save' : 'create'} case intake`));
    } finally {
      setSubmitting(false);
    }
  };

  const renderOverlayFields = () => {
    const q = form.questionnaireData;
    const setOverlay = (overlay: string, key: string, value: any) => {
      setForm((p) => ({
        ...p,
        questionnaireData: {
          ...p.questionnaireData,
          [overlay]: {
            ...(p.questionnaireData as any)[overlay],
            [key]: value,
          },
        },
      }));
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {form.matterOverlays.length === 0 && (
          <Alert severity="info">
            No matter overlays selected. You can still save a general intake summary and add
            overlays later.
          </Alert>
        )}

        {form.matterOverlays.includes('personal_injury') && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>Personal injury</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                {!isFieldHidden('incidentDate') && (
                  <TextField
                    fullWidth
                    type="date"
                    required={isFieldRequired('incidentDate')}
                    label={fieldLabel('Incident date', isFieldRequired('incidentDate'))}
                    InputLabelProps={{ shrink: true }}
                    value={q.personalInjury.incidentDate}
                    onChange={(e) => setOverlay('personalInjury', 'incidentDate', e.target.value)}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Incident type</InputLabel>
                  <Select label="Incident type" value={q.personalInjury.incidentType}
                    onChange={(e) => setOverlay('personalInjury', 'incidentType', e.target.value)}>
                    <MenuItem value="motor_vehicle">Motor vehicle</MenuItem>
                    <MenuItem value="premises">Premises / slip & fall</MenuItem>
                    <MenuItem value="workplace">Workplace</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Location" value={q.personalInjury.incidentLocation}
                  onChange={(e) => setOverlay('personalInjury', 'incidentLocation', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={3} label="What happened"
                  value={q.personalInjury.incidentDescription}
                  onChange={(e) => setOverlay('personalInjury', 'incidentDescription', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={2} label="Injuries reported"
                  value={q.personalInjury.injuriesReported}
                  onChange={(e) => setOverlay('personalInjury', 'injuriesReported', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                {!isFieldHidden('medicalProviders') && (
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    required={isFieldRequired('medicalProviders')}
                    label={fieldLabel('Medical providers', isFieldRequired('medicalProviders'))}
                    value={q.personalInjury.medicalProviders}
                    onChange={(e) => setOverlay('personalInjury', 'medicalProviders', e.target.value)}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth multiline rows={2} label="Records requests"
                  value={q.personalInjury.recordsRequests}
                  onChange={(e) => setOverlay('personalInjury', 'recordsRequests', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Liens" value={q.personalInjury.liens}
                  onChange={(e) => setOverlay('personalInjury', 'liens', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                {!isFieldHidden('wageLoss') && (
                  <TextField
                    fullWidth
                    required={isFieldRequired('wageLoss')}
                    label={fieldLabel('Wage loss', isFieldRequired('wageLoss'))}
                    value={q.personalInjury.wageLoss}
                    onChange={(e) => setOverlay('personalInjury', 'wageLoss', e.target.value)}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {!isFieldHidden('policyLimits') && (
                  <TextField
                    fullWidth
                    required={isFieldRequired('policyLimits')}
                    label={fieldLabel('Policy limits', isFieldRequired('policyLimits'))}
                    value={q.personalInjury.policyLimits}
                    onChange={(e) => setOverlay('personalInjury', 'policyLimits', e.target.value)}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {!isFieldHidden('insuranceCarrier') && (
                  <TextField
                    fullWidth
                    required={isFieldRequired('insuranceCarrier')}
                    label={fieldLabel('Insurance carrier', isFieldRequired('insuranceCarrier'))}
                    value={q.personalInjury.insuranceCarrier}
                    onChange={(e) => setOverlay('personalInjury', 'insuranceCarrier', e.target.value)}
                  />
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        {form.matterOverlays.includes('estate_planning') && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>Estate planning / wills</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Structured roles (Executor, Guardian, Agents, Witnesses, Beneficiaries) are entered on the client’s Related People tab so they reuse across documents. Use the fields below for matter-specific bequests and directives.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Goals" multiline rows={2}
                  value={q.estatePlanning.estatePlanningGoals}
                  onChange={(e) => setOverlay('estatePlanning', 'estatePlanningGoals', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Number of children"
                  value={q.estatePlanning.numberOfChildren}
                  onChange={(e) => setOverlay('estatePlanning', 'numberOfChildren', e.target.value)}
                  helperText="{{Number_of_Children}}" />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField fullWidth label="Proposed fiduciaries (names — optional notes)" multiline rows={2}
                  value={q.estatePlanning.fiduciaryNominations}
                  onChange={(e) => setOverlay('estatePlanning', 'fiduciaryNominations', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                {!isFieldHidden('beneficiaries') && (
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    required={isFieldRequired('beneficiaries')}
                    label={fieldLabel('Intended beneficiaries (specific gifts)', isFieldRequired('beneficiaries'))}
                    value={q.estatePlanning.beneficiaries}
                    onChange={(e) => setOverlay('estatePlanning', 'beneficiaries', e.target.value)}
                    helperText="First name fills {{Beneficiary1_FullName}} if no Related People beneficiary"
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth multiline rows={2} label="Residual beneficiaries"
                  value={q.estatePlanning.residualBeneficiaries}
                  onChange={(e) => setOverlay('estatePlanning', 'residualBeneficiaries', e.target.value)}
                  helperText="Prefer Related People type Residual beneficiary for {{Residual_Beneficiary1_FullName}}" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Additional specific bequests" multiline rows={2}
                  value={q.estatePlanning.additionalSpecificBequests}
                  onChange={(e) => setOverlay('estatePlanning', 'additionalSpecificBequests', e.target.value)}
                  helperText="{{Additional_Specific_Bequests}}" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Healthcare directive / medical instructions" multiline rows={2}
                  value={q.estatePlanning.healthcareDirective}
                  onChange={(e) => setOverlay('estatePlanning', 'healthcareDirective', e.target.value)}
                  helperText="{{Healthcare_Directive}}" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Existing estate documents" multiline rows={2}
                  value={q.estatePlanning.existingEstateDocuments}
                  onChange={(e) => setOverlay('estatePlanning', 'existingEstateDocuments', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                {!isFieldHidden('assetSchedule') && (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    required={isFieldRequired('assetSchedule')}
                    label={fieldLabel('Asset schedule', isFieldRequired('assetSchedule'))}
                    value={q.estatePlanning.assetSchedule}
                    onChange={(e) => setOverlay('estatePlanning', 'assetSchedule', e.target.value)}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {!isFieldHidden('liabilitySchedule') && (
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    required={isFieldRequired('liabilitySchedule')}
                    label={fieldLabel('Liability schedule', isFieldRequired('liabilitySchedule'))}
                    value={q.estatePlanning.liabilitySchedule}
                    onChange={(e) => setOverlay('estatePlanning', 'liabilitySchedule', e.target.value)}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {!isFieldHidden('beneficiaryDesignations') && (
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    required={isFieldRequired('beneficiaryDesignations')}
                    label={fieldLabel(
                      'Beneficiary designations (accounts/policies)',
                      isFieldRequired('beneficiaryDesignations')
                    )}
                    value={q.estatePlanning.beneficiaryDesignations}
                    onChange={(e) =>
                      setOverlay('estatePlanning', 'beneficiaryDesignations', e.target.value)
                    }
                  />
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        {form.matterOverlays.includes('trusts') && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>Trusts</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                {!isFieldHidden('trustType') && (
                  <TextField
                    fullWidth
                    required={isFieldRequired('trustType')}
                    label={fieldLabel('Trust type', isFieldRequired('trustType'))}
                    value={q.trusts.trustType}
                    onChange={(e) => setOverlay('trusts', 'trustType', e.target.value)}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                {!isFieldHidden('grantors') && (
                  <TextField
                    fullWidth
                    required={isFieldRequired('grantors')}
                    label={fieldLabel('Grantors', isFieldRequired('grantors'))}
                    value={q.trusts.grantors}
                    onChange={(e) => setOverlay('trusts', 'grantors', e.target.value)}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                {!isFieldHidden('trustees') && (
                  <TextField
                    fullWidth
                    required={isFieldRequired('trustees')}
                    label={fieldLabel('Trustees', isFieldRequired('trustees'))}
                    value={q.trusts.trustees}
                    onChange={(e) => setOverlay('trusts', 'trustees', e.target.value)}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Beneficiaries" multiline rows={2}
                  value={q.trusts.beneficiaries}
                  onChange={(e) => setOverlay('trusts', 'beneficiaries', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Asset schedule" multiline rows={3}
                  value={q.trusts.assetSchedule}
                  onChange={(e) => setOverlay('trusts', 'assetSchedule', e.target.value)} />
              </Grid>
            </Grid>
          </Box>
        )}

        {form.matterOverlays.includes('financial_elder') && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>Financial / elder</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={q.financialElder.capacityConcerns}
                      onChange={(e) => setOverlay('financialElder', 'capacityConcerns', e.target.checked)}
                    />
                  }
                  label="Capacity concerns identified (flag for attorney — do not diagnose)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Caregivers" value={q.financialElder.caregivers}
                  onChange={(e) => setOverlay('financialElder', 'caregivers', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Power of attorney status"
                  value={q.financialElder.powerOfAttorneyStatus}
                  onChange={(e) => setOverlay('financialElder', 'powerOfAttorneyStatus', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Government benefits" multiline rows={2}
                  value={q.financialElder.governmentBenefits}
                  onChange={(e) => setOverlay('financialElder', 'governmentBenefits', e.target.value)} />
              </Grid>
            </Grid>
          </Box>
        )}

        {form.matterOverlays.includes('real_estate') && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>Real estate / land</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {!isFieldHidden('propertyAddress') && (
                  <TextField
                    fullWidth
                    required={isFieldRequired('propertyAddress')}
                    label={fieldLabel('Property address', isFieldRequired('propertyAddress'))}
                    value={q.realEstate.propertyAddress}
                    onChange={(e) => setOverlay('realEstate', 'propertyAddress', e.target.value)}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Property type</InputLabel>
                  <Select label="Property type" value={q.realEstate.propertyType}
                    onChange={(e) => setOverlay('realEstate', 'propertyType', e.target.value)}>
                    <MenuItem value="residential">Residential</MenuItem>
                    <MenuItem value="commercial">Commercial</MenuItem>
                    <MenuItem value="vacant_land">Vacant land</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Transaction</InputLabel>
                  <Select label="Transaction" value={q.realEstate.transactionType}
                    onChange={(e) => setOverlay('realEstate', 'transactionType', e.target.value)}>
                    <MenuItem value="purchase">Purchase</MenuItem>
                    <MenuItem value="sale">Sale</MenuItem>
                    <MenuItem value="lease">Lease</MenuItem>
                    <MenuItem value="title">Title / boundary</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth type="date" label="Closing date" InputLabelProps={{ shrink: true }}
                  value={q.realEstate.closingDate}
                  onChange={(e) => setOverlay('realEstate', 'closingDate', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Purchase price" value={q.realEstate.purchasePrice}
                  onChange={(e) => setOverlay('realEstate', 'purchasePrice', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={2} label="Known title / boundary issues"
                  value={q.realEstate.knownTitleIssues}
                  onChange={(e) => setOverlay('realEstate', 'knownTitleIssues', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                {!isFieldHidden('parcelId') && (
                  <TextField
                    fullWidth
                    required={isFieldRequired('parcelId')}
                    label={fieldLabel('Parcel ID', isFieldRequired('parcelId'))}
                    value={q.realEstate.parcelId}
                    onChange={(e) => setOverlay('realEstate', 'parcelId', e.target.value)}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {!isFieldHidden('titleCompany') && (
                  <TextField
                    fullWidth
                    required={isFieldRequired('titleCompany')}
                    label={fieldLabel('Title company', isFieldRequired('titleCompany'))}
                    value={q.realEstate.titleCompany}
                    onChange={(e) => setOverlay('realEstate', 'titleCompany', e.target.value)}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {!isFieldHidden('earnestMoney') && (
                  <TextField
                    fullWidth
                    required={isFieldRequired('earnestMoney')}
                    label={fieldLabel('Earnest money', isFieldRequired('earnestMoney'))}
                    value={q.realEstate.earnestMoney}
                    onChange={(e) => setOverlay('realEstate', 'earnestMoney', e.target.value)}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Closing checklist notes" multiline rows={2}
                  value={q.realEstate.closingChecklistNotes}
                  onChange={(e) => setOverlay('realEstate', 'closingChecklistNotes', e.target.value)} />
              </Grid>
            </Grid>
          </Box>
        )}

        {form.matterOverlays.includes('general_litigation') && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>General litigation</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Dispute type" value={q.generalLitigation.disputeType}
                  onChange={(e) => setOverlay('generalLitigation', 'disputeType', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Court case number" value={q.generalLitigation.courtCaseNumber}
                  onChange={(e) => setOverlay('generalLitigation', 'courtCaseNumber', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={3} label="Claim / dispute summary"
                  value={q.generalLitigation.claimSummary}
                  onChange={(e) => setOverlay('generalLitigation', 'claimSummary', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type="date" label="Next hearing / deadline" InputLabelProps={{ shrink: true }}
                  value={q.generalLitigation.nextHearingDate}
                  onChange={(e) => setOverlay('generalLitigation', 'nextHearingDate', e.target.value)} />
              </Grid>
            </Grid>
          </Box>
        )}

        {form.matterOverlays.includes('family') && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>Family</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Relationship to opposing party"
                  value={q.family.opposingPartyRelationship}
                  onChange={(e) => setOverlay('family', 'opposingPartyRelationship', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth type="date" label="Marriage date" InputLabelProps={{ shrink: true }}
                  value={q.family.marriageDate}
                  onChange={(e) => setOverlay('family', 'marriageDate', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField fullWidth type="date" label="Separation date" InputLabelProps={{ shrink: true }}
                  value={q.family.separationDate}
                  onChange={(e) => setOverlay('family', 'separationDate', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={2} label="Children involved in matter"
                  value={q.family.childrenInMatter}
                  onChange={(e) => setOverlay('family', 'childrenInMatter', e.target.value)} />
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    );
  };

  const showIntakeLoading = open && isContinue && Boolean(caseId) && !intakeReady && !loadError;
  const showIntakeForm = !isContinue || !caseId || intakeReady;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">{isContinue ? 'Continue case intake' : 'New case intake'}</Typography>
        <IconButton onClick={onClose}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {showIntakeLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : showIntakeForm ? (
        <>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Matter title" value={form.title}
                onChange={(e) => setField('title', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ClientAutocomplete
                label="Client"
                required
                value={form.clientId || null}
                onChange={(c) => setField('clientId', c?.id || '')}
                disabled={isContinue}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Jurisdiction</InputLabel>
                <Select
                  label="Jurisdiction"
                  value={form.jurisdiction}
                  onChange={(e) => setField('jurisdiction', e.target.value)}
                >
                  <MenuItem value="">
                    <em>Not specified</em>
                  </MenuItem>
                  {US_JURISDICTIONS.map((j) => (
                    <MenuItem key={j.code} value={j.code}>
                      {j.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Court / venue"
                placeholder="e.g. Superior Court of California, County of Los Angeles"
                value={form.court}
                onChange={(e) => setField('court', e.target.value)}
                helperText="Used for {{Court_Name}} on plaintiff templates"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Responsible attorney</InputLabel>
                <Select label="Responsible attorney" value={form.assignedTo}
                  onChange={(e) => setField('assignedTo', e.target.value)}>
                  <MenuItem value="">Unassigned</MenuItem>
                  {users.map((u: any) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.firstName} {u.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Case type label" value={form.caseType}
                onChange={(e) => setField('caseType', e.target.value)}
                placeholder="e.g. Motor vehicle PI" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Urgency</InputLabel>
                <Select label="Urgency" value={form.urgencyLevel}
                  onChange={(e) => setField('urgencyLevel', e.target.value)}>
                  <MenuItem value="routine">Routine</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="statute_risk">Statute / deadline risk</MenuItem>
                  <MenuItem value="emergency">Emergency</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select label="Priority" value={form.priority}
                  onChange={(e) => setField('priority', e.target.value)}>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Matter overlays</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {OVERLAYS.map((o) => (
                  <Chip
                    key={o.id}
                    label={o.label}
                    color={form.matterOverlays.includes(o.id) ? 'primary' : 'default'}
                    variant={form.matterOverlays.includes(o.id) ? 'filled' : 'outlined'}
                    onClick={() => toggleOverlay(o.id)}
                  />
                ))}
              </Box>
            </Grid>
            {intakeRules?.stepHints?.matterBasics && (
              <Grid item xs={12}>
                <Alert severity="success" variant="outlined">
                  {intakeRules.stepHints.matterBasics}
                </Alert>
              </Grid>
            )}
            {intakeRules?.notes && intakeRules.notes.length > 0 && (
              <Grid item xs={12}>
                {intakeRules.notes.map((note) => (
                  <Alert key={note} severity="info" sx={{ mb: 1 }}>
                    {note}
                  </Alert>
                ))}
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={4} required
                label="Intake summary (neutral factual summary)"
                value={form.intakeSummary}
                onChange={(e) => setField('intakeSummary', e.target.value)} />
            </Grid>
          </Grid>
        )}

        {activeStep === 1 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Enter adverse parties and other related names for conflict screening before engagement.
            </Alert>
            {form.adverseParties.map((party, idx) => (
              <Grid container spacing={1} key={idx} sx={{ mb: 1 }} alignItems="center">
                <Grid item xs={12} sm={6} md={2}>
                  <TextField fullWidth size="small" label="First name" value={party.firstName}
                    onChange={(e) => {
                      const next = [...form.adverseParties];
                      next[idx] = { ...next[idx], firstName: e.target.value };
                      setField('adverseParties', next);
                    }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField fullWidth size="small" label="Last name" value={party.lastName}
                    onChange={(e) => {
                      const next = [...form.adverseParties];
                      next[idx] = { ...next[idx], lastName: e.target.value };
                      setField('adverseParties', next);
                    }} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField fullWidth size="small" label="Organization" value={party.organizationName}
                    onChange={(e) => {
                      const next = [...form.adverseParties];
                      next[idx] = { ...next[idx], organizationName: e.target.value };
                      setField('adverseParties', next);
                    }} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Also known as / alias"
                    value={party.alias || ''}
                    onChange={(e) => {
                      const next = [...form.adverseParties];
                      next[idx] = { ...next[idx], alias: e.target.value };
                      setField('adverseParties', next);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={1}>
                  <IconButton
                    aria-label="Remove party"
                    onClick={() => setField(
                      'adverseParties',
                      form.adverseParties.filter((_, i) => i !== idx)
                    )}
                    disabled={form.adverseParties.length === 1}
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button startIcon={<Add />} sx={{ mb: 2 }}
              onClick={() => setField('adverseParties', [...form.adverseParties, emptyAdverse()])}>
              Add adverse party
            </Button>
            <TextField fullWidth multiline rows={3} sx={{ mb: 2 }}
              label="Other related names for conflict check (comma or line separated)"
              helperText="Spouses, insurers, employers, prior counsel, affiliates"
              value={form.relatedNames}
              onChange={(e) => setField('relatedNames', e.target.value)} />
            <Button variant="outlined" onClick={runConflictPreview} disabled={checkingConflict}>
              {checkingConflict ? <CircularProgress size={20} /> : 'Run conflict check'}
            </Button>
            {conflictPreview && (
              <Box sx={{ mt: 2 }}>
                <Alert
                  severity={
                    conflictPreview.status === 'cleared' ? 'success'
                      : conflictPreview.status === 'pending' ? 'info' : 'warning'
                  }
                  sx={{ mb: 1 }}
                >
                  Conflict status: <strong>{conflictPreview.status}</strong>
                  {conflictPreview.matches?.length
                    ? ` — ${conflictPreview.matches.length} match(es)`
                    : ' — no matches'}
                </Alert>
                {conflictPreview.matches?.length > 0 && (
                  <List dense>
                    {conflictPreview.matches.slice(0, 12).map((m: any) => (
                      <ListItem key={`${m.source}-${m.id}`}>
                        <ListItemText
                          primary={`${m.label} (${m.severity})`}
                          secondary={`${m.source}${m.detail ? ` · ${m.detail}` : ''}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel>Recorded conflict decision</InputLabel>
                  <Select
                    label="Recorded conflict decision"
                    value={form.conflictCheckStatus || conflictPreview.status}
                    onChange={(e) => setField('conflictCheckStatus', e.target.value)}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="cleared">Cleared</MenuItem>
                    <MenuItem value="potential_conflict">Potential conflict</MenuItem>
                    <MenuItem value="waived">Waived</MenuItem>
                    <MenuItem value="declined">Declined representation</MenuItem>
                  </Select>
                </FormControl>
                <TextField fullWidth sx={{ mt: 2 }} multiline rows={2} label="Conflict notes"
                  value={form.conflictCheckNotes}
                  onChange={(e) => setField('conflictCheckNotes', e.target.value)} />
              </Box>
            )}
          </Box>
        )}

        {activeStep === 2 && (
          <>
            {intakeRules?.stepHints?.matterDetails && (
              <Alert severity="success" variant="outlined" sx={{ mb: 2 }}>
                {intakeRules.stepHints.matterDetails}
              </Alert>
            )}
            {intakeRules?.notes && intakeRules.notes.length > 0 && (
              <Box sx={{ mb: 2 }}>
                {intakeRules.notes.map((note) => (
                  <Alert key={note} severity="info" sx={{ mb: 1 }}>
                    {note}
                  </Alert>
                ))}
              </Box>
            )}
            {renderOverlayFields()}
          </>
        )}

        {activeStep === 3 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Representation status</InputLabel>
                <Select label="Representation status" value={form.representationStatus}
                  onChange={(e) => setField('representationStatus', e.target.value)}>
                  <MenuItem value="prospective">Prospective</MenuItem>
                  <MenuItem value="pending_engagement">Pending engagement</MenuItem>
                  <MenuItem value="engaged">Engaged</MenuItem>
                  <MenuItem value="declined">Declined</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox checked={form.privacyNoticeAcknowledged}
                      onChange={(e) => setField('privacyNoticeAcknowledged', e.target.checked)} />
                  }
                  label="Privacy notice acknowledged"
                />
                <FormControlLabel
                  control={
                    <Checkbox checked={form.communicationConsent}
                      onChange={(e) => setField('communicationConsent', e.target.checked)} />
                  }
                  label="Electronic communication consent"
                />
                <FormControlLabel
                  control={
                    <Checkbox checked={form.retainerSent}
                      onChange={(e) => setField('retainerSent', e.target.checked)} />
                  }
                  label="Retainer agreement sent"
                />
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Typed signature / acknowledger name"
                value={form.signatureName}
                onChange={(e) => setField('signatureName', e.target.value)}
                helperText="Optional staff-captured acknowledgment name" />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Client can also complete typed e-sign acknowledgments later in the client portal
                (privacy, communication, retainer).
              </Typography>
            </Grid>
          </Grid>
        )}
        </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!loadError && (
          <>
        <Button disabled={activeStep === 0 || showIntakeLoading} onClick={() => setActiveStep((s) => s - 1)}>Back</Button>
        {activeStep < STEPS.length - 1 ? (
          <Button variant="contained" disabled={!canNext || showIntakeLoading} onClick={() => setActiveStep((s) => s + 1)}>
            Next
          </Button>
        ) : (
          <Button variant="contained" disabled={submitting || !canNext || showIntakeLoading} onClick={handleSubmit}>
            {submitting ? <CircularProgress size={20} /> : isContinue ? 'Save intake' : 'Create case intake'}
          </Button>
        )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CaseIntakeWizard;
